import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { get } from 'lodash'
import { RefreshCw, CalendarDays } from 'lucide-react'

import { Button } from '../../../ui/button'
import { Checkbox } from '../../../ui/checkbox'
import { Badge } from '../../../ui/badge'

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', full: 'Sunday' },
  { value: 1, label: 'Mon', full: 'Monday' },
  { value: 2, label: 'Tue', full: 'Tuesday' },
  { value: 3, label: 'Wed', full: 'Wednesday' },
  { value: 4, label: 'Thu', full: 'Thursday' },
  { value: 5, label: 'Fri', full: 'Friday' },
  { value: 6, label: 'Sat', full: 'Saturday' }
]

const WEEKS_TO_GENERATE = 52

function getFirstUpcomingDay(dayValue) {
  const today = moment()
  const todayDay = today.day()

  if (todayDay === dayValue) {
    return today.clone().startOf('day')
  }

  const daysAhead = (dayValue - todayDay + 7) % 7
  return today.clone().add(daysAhead, 'days').startOf('day')
}

function MultiSelectDayDateTime({ className, value: dates = [], onChange, error }) {
  const [selectedDays, setSelectedDays] = useState(() => {
    if (dates.length > 0) {
      return [...new Set(dates.map((d) => d.date.day()))].sort()
    }
    return []
  })

  const [classDays, setClassDays] = useState(dates || [])

  const datesKey = useMemo(
    () =>
      (dates || [])
        .filter((d) => d.isTimeSet)
        .map((d) => (d.date?.format ? d.date.format('YYYY-MM-DD HH:mm') : ''))
        .sort()
        .join('|'),
    [dates]
  )

  useEffect(() => {
    setClassDays(dates || [])
    if (dates?.length > 0) {
      setSelectedDays([...new Set(dates.map((d) => d.date.day()))].sort())
    } else {
      setSelectedDays([])
    }
  }, [datesKey, dates])

  const availableDates = useMemo(() => {
    const allDates = []

    selectedDays.forEach((dayValue) => {
      const firstDay = getFirstUpcomingDay(dayValue)
      for (let i = 0; i < WEEKS_TO_GENERATE; i++) {
        allDates.push(firstDay.clone().add(i, 'w'))
      }
    })

    return allDates.sort((a, b) => a.valueOf() - b.valueOf())
  }, [selectedDays])

  function toggleDay(dayValue) {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        const newDays = prev.filter((d) => d !== dayValue)
        const newClassDays = classDays.filter((cd) => cd.date.day() !== dayValue)
        setClassDays(newClassDays)
        onChange(newClassDays)
        return newDays
      }
      return [...prev, dayValue].sort()
    })
  }

  function isDateSelected(date) {
    return !!classDays.find((classDay) => date.isSame(classDay.date, 'day'))
  }

  function toggleDate(date) {
    if (isDateSelected(date)) {
      const newClassDays = classDays.filter((classDay) => !date.isSame(classDay.date, 'day'))
      setClassDays(newClassDays)
      onChange(newClassDays)
    } else {
      let newClassDays = [...classDays, { date: date, isTimeSet: false }]
      newClassDays = newClassDays.sort((a, b) => a.date.valueOf() - b.date.valueOf())
      setClassDays(newClassDays)
      onChange(newClassDays)
    }
  }

  function syncAllTimesTo(classDay) {
    if (classDay.isTimeSet) {
      const newClassDays = classDays.map((cd) => ({
        date: cd.date.clone().set({
          hour: classDay.date.hour(),
          minute: classDay.date.minute()
        }),
        isTimeSet: true
      }))
      setClassDays(newClassDays)
      onChange(newClassDays)
    }
  }

  function selectAllDatesForSelectedDays() {
    const allNewClassDays = [...classDays]
    availableDates.forEach((date) => {
      if (!isDateSelected(date)) {
        allNewClassDays.push({ date: date.clone(), isTimeSet: false })
      }
    })
    allNewClassDays.sort((a, b) => a.date.valueOf() - b.date.valueOf())
    setClassDays(allNewClassDays)
    onChange(allNewClassDays)
  }

  function clearAllDates() {
    setClassDays([])
    onChange([])
  }

  const selectedDayLabels = selectedDays.map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.label).join(' / ')

  return (
    <div className={className}>
      <div className="mb-5">
        <label className="block mb-2 text-sm font-medium text-gray-900">
          What days will this class run?
        </label>
        <div className="flex flex-wrap gap-2">
          {DAYS_OF_WEEK.map((day) => {
            const isActive = selectedDays.includes(day.value)
            return (
              <Badge
                key={day.value}
                variant={isActive ? 'default' : 'outline'}
                className={`
                  cursor-pointer select-none px-3.5 py-1.5 text-sm transition-all duration-150
                  ${isActive ? 'bg-purple-600 hover:bg-purple-700 border-purple-600 text-white' : 'hover:bg-gray-100 hover:border-gray-400'}
                `}
                onClick={() => toggleDay(day.value)}
                data-cy={`day-toggle-${day.label.toLowerCase()}`}
              >
                {day.label}
              </Badge>
            )
          })}
        </div>
        {selectedDays.length > 1 && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-purple-600 font-medium">
            <CalendarDays className="h-3.5 w-3.5" />
            <span>Multi-day class: {selectedDayLabels}</span>
          </div>
        )}
      </div>

      {selectedDays.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              {classDays.length} class {classDays.length === 1 ? 'day' : 'days'} selected
            </p>
            <div className="flex items-center gap-3">
              {classDays.length > 0 && classDays.length < availableDates.length && (
                <button
                  type="button"
                  onClick={selectAllDatesForSelectedDays}
                  className="text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
                >
                  Select all
                </button>
              )}
              {classDays.length > 0 && (
                <button
                  type="button"
                  onClick={clearAllDates}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Clear
                </button>
              )}
              {classDays.length > 1 && (
                <p className="text-xs text-gray-500">
                  {moment(classDays[0].date).format('MMM D')} &ndash;{' '}
                  {moment(classDays[classDays.length - 1].date).format('MMM D')}
                </p>
              )}
            </div>
          </div>

          <div
            className={`h-64 mb-2 overflow-y-auto border rounded-lg bg-gray-50 ${
              get(error, 'message') ? 'border-red-500' : 'border-gray-200'
            }`}
          >
            {availableDates.map((date) => {
              const selected = isDateSelected(date)
              return (
                <div
                  key={date.valueOf()}
                  className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 transition-colors ${
                    selected ? 'bg-purple-50 hover:bg-purple-100' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => toggleDate(date)}
                    data-cy={date.format('MMM D YYYY')}
                  >
                    <Checkbox checked={selected} data-cy={`${date}-check-button`} />
                    <div className="flex items-center gap-2">
                      {selectedDays.length > 1 && (
                        <span className="inline-flex items-center justify-center w-9 text-[10px] font-bold uppercase tracking-wide text-purple-700 bg-purple-100 rounded px-1 py-0.5">
                          {date.format('ddd')}
                        </span>
                      )}
                      <p
                        className={`text-sm font-medium ${
                          selected ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {date.format('MMM D, YYYY')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected && (
                      <>
                        <input
                          type="time"
                          className={`px-3 py-2 text-sm bg-white text-gray-900 border rounded-md shadow-sm transition-colors
                            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                            w-32
                            ${
                              get(error, 'dates') && error.dates.find((errorDate) => date.isSame(errorDate))
                                ? 'bg-red-100 border-red-500'
                                : 'border-gray-300'
                            }
                          `}
                          onChange={(e) => {
                            if (e.target.value) {
                              const [hours, minutes] = e.target.value.split(':')
                              const newClassDays = classDays.map((classDay) =>
                                date.isSame(classDay.date, 'day')
                                  ? {
                                      isTimeSet: true,
                                      date: classDay.date.clone().set({
                                        hour: parseInt(hours, 10),
                                        minute: parseInt(minutes, 10)
                                      })
                                    }
                                  : classDay
                              )
                              setClassDays(newClassDays)
                              onChange(newClassDays)
                            }
                          }}
                          value={
                            classDays.find((classDay) => date.isSame(classDay.date, 'day'))?.isTimeSet
                              ? classDays.find((classDay) => date.isSame(classDay.date, 'day')).date.format('HH:mm')
                              : ''
                          }
                          data-cy={`${date.format('MMM D YYYY')}-time`}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={!classDays.find((classDay) => date.isSame(classDay.date, 'day'))?.isTimeSet}
                          onClick={() =>
                            syncAllTimesTo(classDays.find((classDay) => date.isSame(classDay.date, 'day')))
                          }
                          data-cy="sync-time-button"
                          className="h-8 w-8 p-0"
                          title="Sync this time to all selected dates"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {get(error, 'message') && <p className="text-sm text-red-500">{get(error, 'message')}</p>}
        </>
      )}

      {selectedDays.length === 0 && (
        <div className="flex flex-col items-center justify-center h-48 rounded-lg border border-dashed border-gray-300 bg-gray-50">
          <CalendarDays className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">Select one or more days above to get started</p>
        </div>
      )}
    </div>
  )
}

MultiSelectDayDateTime.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.array,
  className: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    dates: PropTypes.array
  })
}

export default MultiSelectDayDateTime
